import { useQuery } from '@tanstack/react-query';

import {
  BinLocationCompact,
  ListMetaWoltWarehouseItems,
  ListMetaWarehouseItems,
  WoltWarehouseItem,
  WarehouseItem,
  BatchesForDryRunResult,
  CountLowInventoryItemsDryRunResult,
  ShrinkExpiredItemsDryRunResult,
  ShrinkItemsExpiringTodayDryRunResult,
  CountUnreliableItemsDryRunResult,
  MoveToBeDiscountedDryRunResult,
  DryRunEligibilitySummaryResult,
  WarehouseTaskType,
} from 'common/api-types/warehouse';
import { UseQueryConfig } from 'common/query/types';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { usePersistedApiQuery } from 'common/query/usePersistedApiQuery';
import { AxiosWarehouseError } from 'common/types';
import {
  fetchWoltItem,
  fetchItem,
  searchWoltItems,
  FetchItemsParams,
  fetchItems,
  fetchCompactBinLocations,
  fetchBatchesForDryRun,
  fetchDryRunCountLowInventoryItems,
  fetchDryRunEligibilitySummary,
  fetchDryRunShrinkExpiredItems,
  fetchDryRunShrinkItemsExpiringToday,
  fetchDryRunCountUnreliableItems,
  fetchDryRunMoveToBeDiscounted,
} from 'features/warehouse/common/api';

export const WAREHOUSE_ITEM_QUERY_KEY_ROOT = 'warehouseItem';
export const WAREHOUSE_ITEM_QUERY_KEY_ROOT_V2 = 'warehouseItemV2';
export const WAREHOUSE_ITEMS_QUERY_KEY_ROOT = 'warehouseItems';
export const WAREHOUSE_ITEMS_QUERY_KEY_ROOT_V2 = 'warehouseItemsV2';

export const useWoltItemQuery = ({
  warehouseId,
  itemId,
  options,
}: {
  warehouseId: string;
  itemId: string;
  options?: UseQueryConfig<WoltWarehouseItem, AxiosWarehouseError>;
}) => {
  const queryClient = useApiQueryClient();

  return useQuery({
    enabled: !!warehouseId && !!itemId,
    ...options,
    queryKey: [
      WAREHOUSE_ITEM_QUERY_KEY_ROOT,
      {
        warehouseId,
        itemId,
      },
    ],
    queryFn: () => fetchWoltItem(warehouseId, itemId),
    initialData: () => {
      const queries = queryClient.getQueriesData<ListMetaWoltWarehouseItems>({
        queryKey: [WAREHOUSE_ITEMS_QUERY_KEY_ROOT],
        type: 'active',
      });

      const latestQuery = queries[queries.length - 1];

      return latestQuery?.[1]?.items?.find((item) => item.id === itemId);
    },
  });
};

export const useItemQuery = ({
  warehouseId,
  itemId,
  countryCode,
  options,
}: {
  warehouseId: string;
  itemId: string;
  countryCode?: string;
  options?: UseQueryConfig<WarehouseItem, AxiosWarehouseError>;
}) => {
  const queryClient = useApiQueryClient();

  return useQuery({
    ...options,
    queryKey: [
      WAREHOUSE_ITEM_QUERY_KEY_ROOT_V2,
      {
        warehouseId,
        itemId,
      },
    ],
    queryFn: () => fetchItem(warehouseId, itemId, countryCode),
    initialData: () => {
      const queries = queryClient.getQueriesData<ListMetaWarehouseItems>({
        queryKey: [WAREHOUSE_ITEMS_QUERY_KEY_ROOT_V2],
        type: 'active',
      });

      const latestQuery = queries[queries.length - 1];

      return latestQuery?.[1]?.items?.find((item) => item.product?.id === itemId);
    },
  });
};

export const useWoltItemsQuery = (
  warehouseId: string,
  params: FetchItemsParams,
  options?: UseQueryConfig<ListMetaWoltWarehouseItems, AxiosWarehouseError>,
) => {
  return useQuery({
    ...options,
    queryKey: [
      WAREHOUSE_ITEMS_QUERY_KEY_ROOT,
      {
        searchQuery: params.q,
        warehouseId,
      },
    ],
    queryFn: () => searchWoltItems(warehouseId, params),
  });
};

export const useItemsQuery = (
  warehouseId: string,
  params: FetchItemsParams,
  options?: UseQueryConfig<ListMetaWarehouseItems, AxiosWarehouseError>,
) => {
  return useQuery({
    ...options,
    queryKey: [
      WAREHOUSE_ITEMS_QUERY_KEY_ROOT_V2,
      {
        searchQuery: params.q,
        warehouseId,
        fromExpiryDate: params.fromExpiryDate,
        toExpiryDate: params.toExpiryDate,
      },
    ],
    queryFn: () => fetchItems(warehouseId, params),
  });
};

export const useCompactBinLocationsQuery = (
  warehouseId?: string,
  options?: UseQueryConfig<BinLocationCompact[], AxiosWarehouseError>,
) => {
  return useQuery({
    ...options,
    queryKey: ['binLocations', 'compact-list', { warehouseId }],
    queryFn: () => fetchCompactBinLocations(warehouseId as string),
    enabled: !!warehouseId && (options?.enabled ?? true),
  });
};

export const usePersistedCompactBinLocationsQuery = (warehouseId?: string) => {
  return usePersistedApiQuery({
    queryKey: ['binLocations', 'compact-list', { warehouseId }],
    queryFn: () => fetchCompactBinLocations(warehouseId as string),
    enabled: !!warehouseId,
  });
};

export const TASK_ELIGIBILITY_QUERY_KEY_ROOT = 'taskEligibility';

export const useBatchesForDryRunQuery = ({
  warehouseId,
  productId,
  taskType,
  options,
}: {
  warehouseId: string;
  productId: string;
  taskType: WarehouseTaskType;
  options?: UseQueryConfig<BatchesForDryRunResult, AxiosWarehouseError>;
}) =>
  useQuery({
    enabled: !!warehouseId && !!productId,
    ...options,
    queryKey: [TASK_ELIGIBILITY_QUERY_KEY_ROOT, 'batches', { warehouseId, productId, taskType }],
    queryFn: () => fetchBatchesForDryRun(warehouseId, productId, taskType),
  });

export const useDryRunCountLowInventoryItemsQuery = ({
  warehouseId,
  productId,
  options,
}: {
  warehouseId: string;
  productId: string;
  options?: UseQueryConfig<CountLowInventoryItemsDryRunResult, AxiosWarehouseError>;
}) =>
  useQuery({
    enabled: !!warehouseId && !!productId,
    ...options,
    queryKey: [TASK_ELIGIBILITY_QUERY_KEY_ROOT, 'lowInventory', { warehouseId, productId }],
    queryFn: () => fetchDryRunCountLowInventoryItems(warehouseId, productId),
  });

export const useDryRunEligibilitySummaryQuery = ({
  warehouseId,
  productId,
  options,
}: {
  warehouseId: string;
  productId: string;
  options?: UseQueryConfig<DryRunEligibilitySummaryResult, AxiosWarehouseError>;
}) =>
  useQuery({
    enabled: !!warehouseId && !!productId,
    ...options,
    queryKey: [TASK_ELIGIBILITY_QUERY_KEY_ROOT, 'eligibilitySummary', { warehouseId, productId }],
    queryFn: () => fetchDryRunEligibilitySummary(warehouseId, productId),
  });

export const useDryRunShrinkExpiredItemsQuery = ({
  warehouseId,
  locationBatchId,
  options,
}: {
  warehouseId: string;
  locationBatchId: string;
  options?: UseQueryConfig<ShrinkExpiredItemsDryRunResult, AxiosWarehouseError>;
}) =>
  useQuery({
    enabled: !!warehouseId && !!locationBatchId,
    ...options,
    queryKey: [TASK_ELIGIBILITY_QUERY_KEY_ROOT, 'shrinkExpired', { warehouseId, locationBatchId }],
    queryFn: () => fetchDryRunShrinkExpiredItems(warehouseId, locationBatchId),
  });

export const useDryRunShrinkItemsExpiringTodayQuery = ({
  warehouseId,
  locationBatchId,
  options,
}: {
  warehouseId: string;
  locationBatchId: string;
  options?: UseQueryConfig<ShrinkItemsExpiringTodayDryRunResult, AxiosWarehouseError>;
}) =>
  useQuery({
    enabled: !!warehouseId && !!locationBatchId,
    ...options,
    queryKey: [
      TASK_ELIGIBILITY_QUERY_KEY_ROOT,
      'shrinkExpiringToday',
      { warehouseId, locationBatchId },
    ],
    queryFn: () => fetchDryRunShrinkItemsExpiringToday(warehouseId, locationBatchId),
  });

export const useDryRunCountUnreliableItemsQuery = ({
  warehouseId,
  locationBatchId,
  options,
}: {
  warehouseId: string;
  locationBatchId: string;
  options?: UseQueryConfig<CountUnreliableItemsDryRunResult, AxiosWarehouseError>;
}) =>
  useQuery({
    enabled: !!warehouseId && !!locationBatchId,
    ...options,
    queryKey: [
      TASK_ELIGIBILITY_QUERY_KEY_ROOT,
      'countUnreliable',
      { warehouseId, locationBatchId },
    ],
    queryFn: () => fetchDryRunCountUnreliableItems(warehouseId, locationBatchId),
  });

export const useDryRunMoveToBeDiscountedQuery = ({
  warehouseId,
  locationBatchId,
  options,
}: {
  warehouseId: string;
  locationBatchId: string;
  options?: UseQueryConfig<MoveToBeDiscountedDryRunResult, AxiosWarehouseError>;
}) =>
  useQuery({
    enabled: !!warehouseId && !!locationBatchId,
    ...options,
    queryKey: [
      TASK_ELIGIBILITY_QUERY_KEY_ROOT,
      'moveToBeDiscounted',
      { warehouseId, locationBatchId },
    ],
    queryFn: () => fetchDryRunMoveToBeDiscounted(warehouseId, locationBatchId),
  });
